import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconGauge } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import { SpeedGlyph, videoTileClass, videoTrayClass } from '../../components/video/VideoOptionTiles';
import {
    DEFAULT_SPEED,
    MAX_SPEED,
    MAX_SPEED_VIDEO_BYTES,
    MIN_SPEED,
    SPEED_PRESETS,
    changeVideoSpeed,
    formatFileSize,
    isSupportedVideoFile,
    speedVideoName,
} from './speedVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-2.5 text-sm text-[#3D3833] outline-none placeholder:text-[#A8A099] focus:border-violet-400 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

function formatSpeedLabel(speed) {
    const n = Number(speed) || DEFAULT_SPEED;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

export default function SpeedVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [speedPreset, setSpeedPreset] = useState('1');
    const [useCustomSpeed, setUseCustomSpeed] = useState(false);
    const [customSpeed, setCustomSpeed] = useState(String(DEFAULT_SPEED));
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const activeSpeed = useMemo(() => {
        if (useCustomSpeed) {
            const n = Number(customSpeed);
            if (!Number.isFinite(n) || n <= 0) return DEFAULT_SPEED;
            return Math.max(MIN_SPEED, Math.min(MAX_SPEED, n));
        }
        return Number(speedPreset) || DEFAULT_SPEED;
    }, [useCustomSpeed, customSpeed, speedPreset]);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('speedVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_SPEED_VIDEO_BYTES) {
            const msg = t('speedVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setSpeedPreset('1');
        setUseCustomSpeed(false);
        setCustomSpeed(String(DEFAULT_SPEED));
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setSpeedPreset('1');
        setUseCustomSpeed(false);
        setCustomSpeed(String(DEFAULT_SPEED));
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('speedVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'SPEED_FAILED') {
            return t('speedVideo.errors.failed');
        }
        return t('speedVideo.errors.failed');
    };

    const handleChangeSpeed = async () => {
        if (!file || processing) return;
        if (!Number.isFinite(activeSpeed) || activeSpeed <= 0) {
            const msg = t('speedVideo.errors.invalidSpeed');
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('video/speed', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await changeVideoSpeed(file, activeSpeed, (value) => setProgress(value));
            const output = {
                name: speedVideoName(file.name, activeSpeed),
                originalSize: file.size,
                outputSize: blob.size,
                speed: activeSpeed,
                blob,
            };
            setResult(output);
            notifications.show({
                title: t('common.success'),
                message: t('speedVideo.successNotice', { speed: toBnDigits(formatSpeedLabel(activeSpeed)) }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file && activeSpeed > 0,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`speedVideo.howTo.step${i}`)),
        [t],
    );

    const speedBadge = t('speedVideo.speedBadge', { speed: toBnDigits(formatSpeedLabel(activeSpeed)) });

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({t('speedVideo.speedBadge', { speed: toBnDigits(formatSpeedLabel(result.speed)) })})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('speedVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/speed"
            subtitleKey="speedVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('speedVideo.apply'),
                onClick: handleChangeSpeed,
                disabled: !file || processing || !!result || activeSpeed <= 0,
                loading: processing,
                icon: <IconGauge size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('speedVideo.dropTitle')}
                            hint={t('speedVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('speedVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('speedVideo.settingsTitle')}>
                    <div className="space-y-4">
                        <div>
                            <p className="mb-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('speedVideo.presetTitle')}
                            </p>
                            {/* A dial per preset, the number under it; slow / normal /
                                fast lives in the tooltip. */}
                            <div className={videoTrayClass('grid-cols-6')}>
                                {SPEED_PRESETS.map((preset) => {
                                    const active = !useCustomSpeed && speedPreset === preset.id;
                                    const name = t(`speedVideo.presets.${preset.id.replace('.', '_')}`);
                                    return (
                                        <button
                                            key={preset.id}
                                            type="button"
                                            onClick={() => {
                                                setSpeedPreset(preset.id);
                                                setUseCustomSpeed(false);
                                                invalidateResult();
                                            }}
                                            title={name}
                                            aria-label={name}
                                            aria-pressed={active}
                                            className={videoTileClass(active)}
                                        >
                                            <SpeedGlyph value={preset.value} />
                                            {t('speedVideo.speedBadge', { speed: toBnDigits(formatSpeedLabel(preset.value)) })}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('speedVideo.customTitle')}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUseCustomSpeed((prev) => !prev);
                                        invalidateResult();
                                    }}
                                    className={`rounded-lg border px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
                                        useCustomSpeed
                                            ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-500 dark:bg-violet-900/30 dark:text-violet-300'
                                            : 'border-[#ECE8E4] text-[#6B635C] dark:border-zinc-700 dark:text-zinc-400'
                                    }`}
                                >
                                    {useCustomSpeed ? t('speedVideo.customOn') : t('speedVideo.customOff')}
                                </button>
                            </div>
                            <label htmlFor="speed-video-custom" className="block">
                                <input
                                    id="speed-video-custom"
                                    type="number"
                                    min={MIN_SPEED}
                                    max={MAX_SPEED}
                                    step={0.05}
                                    value={customSpeed}
                                    onChange={(e) => {
                                        setCustomSpeed(e.target.value);
                                        invalidateResult();
                                    }}
                                    disabled={!useCustomSpeed}
                                    className={inputClass}
                                />
                            </label>
                            <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                {t('speedVideo.customHint')}
                            </p>
                        </div>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('speedVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('speedVideo.previewEmpty')}
                    idleHint={t('speedVideo.previewEmptyHint')}
                    processingLabel={t('speedVideo.processing')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('speedVideo.done')}
                    downloadLabel={t('speedVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('speedVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('speedVideo.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : speedBadge}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
